import { Logger } from '@uploadcam/common';
import { GetAuthUrlInteractor } from './get-auth-url.interactor';
import { LoginWithPopupAdapterInteractor } from './login-with-popup-adapter.interactor';
import { OAuthResponse } from '../models/oauth-response.model';

export class LoginWithPopupGenericAdapterInteractor implements LoginWithPopupAdapterInteractor {
  constructor(
    private readonly getAuthUrl: GetAuthUrlInteractor,
    private readonly logger: Logger,
    private readonly timeout: number,
    private readonly window: Window,
    private readonly windowName: string,
  ) {}

  public async execute(
    state: string,
    pkceChallenge: string,
    authTypeChanged: boolean,
  ): Promise<OAuthResponse> {
    const authUrl = this.getAuthUrl.execute(state, pkceChallenge, authTypeChanged);

    return new Promise((resolve, reject) => {
      let popupWindow: Window | null;

      // Close popup after timeout
      const timeoutTimerId = setTimeout(() => {
        clearTimeout(timeoutTimerId);
        reject(new Error('OAuth request timed out.'));
      }, this.timeout);

      const handlePopupMsg = (event: MessageEvent<OAuthResponse>) => {
        // Only process messages coming from `popupWindow`
        if (event.source !== popupWindow) {
          this.logger.warn('Message from unknown window received');
          return;
        }

        // Clean-up
        clearTimeout(timeoutTimerId);
        window.removeEventListener('message', handlePopupMsg);

        this.logger.info('Message received from the popup.');

        resolve(event.data);
      };

      popupWindow = window.open(
        authUrl,
        this.windowName,
        this.intoFeatures({
          popup: 'true',
          width: 400,
          height: 600,
          top: 'auto',
          left: 'auto',
          toolbar: 'no',
          menubar: 'no',
        }),
      );

      if (popupWindow) {
        this.logger.info('OAuth popup opened.');
        popupWindow.focus();
        window.addEventListener('message', handlePopupMsg, false);
      } else {
        clearTimeout(timeoutTimerId);
        reject(new Error(`Couldn't open the popup window.`));
      }
    });
  }

  private intoFeatures(features: Record<string, string | number>): string {
    return Object.entries(features)
      .map(([key, value]) => {
        if (key === 'top' && value == 'auto') {
          value = Math.round(this.window.innerHeight / 2 - Number(features['height']) / 2);
        } else if (key === 'left' && value == 'auto') {
          value = Math.round(this.window.innerWidth / 2 - Number(features['width']) / 2);
        }

        return `${key}=${value}`;
      })
      .join(',');
  }
}
